import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Button from '../components/Button';
import Field from '../components/Field';
import Select from '../components/Select';
import Callout from '../components/Callout';
import { VERTICALS } from '../data/verticals';
import { api, ApiError } from '../lib/api';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string | undefined;
      remove?: (widgetId: string) => void;
    };
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

export default function RequestAccess() {
  const [form, setForm] = useState({ name: '', email: '', org: '', phone: '', vertical: '', message: '' });
  const [token, setToken] = useState<string | undefined>();
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const widget = useRef<HTMLDivElement>(null);

  /**
   * Mount the Turnstile widget.
   *
   * Turnstile only loads when a site key is configured, so the form works
   * before it is wired up. Once it IS wired up the server rejects tokenless
   * submissions, which makes every failure mode here a broken signup form
   * rather than a cosmetic problem. Hence the care:
   *
   *  - The script is appended once and reused. The previous version created a
   *    new <script> on every mount and rendered only from `onload`; on a second
   *    mount the browser may serve the already-parsed script without firing
   *    load again, and the widget then never renders at all — a form that
   *    cannot be submitted, with nothing visibly wrong.
   *  - The widget is explicitly removed on unmount. Without that, navigating
   *    away and back leaves an orphaned widget and a container that
   *    `render()` refuses to draw into a second time.
   *  - error and expired callbacks clear the token. Tokens die after five
   *    minutes, which is easily less than the time to fill this form, and a
   *    stale token is rejected exactly like no token. Clearing it lets the
   *    submit guard below say so instead of the request failing at the server.
   */
  useEffect(() => {
    if (!SITE_KEY) return;

    let widgetId: string | undefined;
    let cancelled = false;

    const render = () => {
      if (cancelled || !widget.current || !window.turnstile) return;
      // A container that already holds a widget will not accept another.
      widget.current.innerHTML = '';
      widgetId = window.turnstile.render(widget.current, {
        sitekey: SITE_KEY,
        callback: (t: string) => setToken(t),
        'error-callback': () => setToken(undefined),
        'expired-callback': () => setToken(undefined),
        'timeout-callback': () => setToken(undefined),
      });
    };

    if (window.turnstile) {
      render();
    } else {
      const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      let script = document.querySelector<HTMLScriptElement>(`script[src="${SRC}"]`);
      if (!script) {
        script = document.createElement('script');
        script.src = SRC;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', render);
      // The script is left in place deliberately: it is shared, and removing
      // it would break any other mount that is waiting on it.
    }

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile?.remove) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // Already gone. Nothing to do, and nothing worth surfacing.
        }
      }
    };
  }, []);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const emailLooksRight = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());
  // Seven digits is the loosest thing that is still a phone number; the field
  // accepts whatever shape people actually type.
  const phoneLooksRight = form.phone.replace(/\D/g, '').length >= 7;
  const valid = form.name.trim().length > 1 && emailLooksRight && phoneLooksRight;

  const submit = async () => {
    if (!valid) {
      setShowErrors(true);
      return;
    }
    // With a site key configured the server rejects a tokenless submission.
    // Saying so here beats letting it fail at the server, where the message
    // reads like an accusation rather than "wait a second and try again".
    if (SITE_KEY && !token) {
      setError('Still checking you are a person — give it a second, then try again.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await api.requestAccess({ ...form, turnstileToken: token });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not send. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <AppShell>
        <p className="t-eyebrow text-green-600">Request sent</p>
        <h1 className="t-h1 mt-3 text-ink">We have it</h1>
        <p className="t-body-lg measure mt-4 text-grey-600">
          The team reads these, usually within a business day. You will get an email
          with a link to sign in once it is approved. Nothing else is needed from you.
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell action={<Link to="/login" className="t-meta min-h-tap inline-flex items-center font-semibold text-green-700">Sign in</Link>}>
      <p className="t-eyebrow text-green-600">Request access</p>
      <h1 className="t-h1 mt-3 text-ink">Tell us what you are building</h1>
      <p className="t-body-lg measure mt-4 text-grey-600">
        Access is opened by hand, so we know who is using it. Name, email, and phone are
        needed; the rest helps us have a better first conversation.
      </p>

      {error && (
        <div className="mt-8">
          <Callout tone="caution" title="That did not send">{error}</Callout>
        </div>
      )}

      <div className="mt-10 grid max-w-3xl gap-7 sm:grid-cols-2">
        <Field
          id="ra-name"
          label="Your name"
          required
          value={form.name}
          onChange={set('name')}
          error={showErrors && form.name.trim().length < 2 ? 'We need a name to address you by.' : undefined}
        />
        <Field
          id="ra-email"
          label="Email"
          type="email"
          required
          hint="This is where the sign-in link goes."
          value={form.email}
          onChange={set('email')}
          error={showErrors && !emailLooksRight ? 'Check this address — the link goes here.' : undefined}
        />
        <Field
          id="ra-phone"
          label="Phone"
          type="tel"
          required
          hint="So we can talk it through rather than trade emails."
          value={form.phone}
          onChange={set('phone')}
          error={showErrors && !phoneLooksRight ? 'We need a phone number we can reach you on.' : undefined}
        />
        <Field id="ra-org" label="Organization" hint="Church, company, school system, or county." value={form.org} onChange={set('org')} />
        <div className="sm:col-span-2">
          <Select
            id="ra-vertical"
            label="What are you building?"
            placeholder="Choose the closest match"
            options={VERTICALS.map((v) => ({ value: v.id, label: v.label }))}
            value={form.vertical}
            onChange={set('vertical')}
          />
        </div>
        <div className="sm:col-span-2">
          <Field
            id="ra-message"
            label="Anything else"
            hint="Where you are with it, roughly when, and what is driving it."
            value={form.message}
            onChange={set('message')}
          />
        </div>
      </div>

      <div ref={widget} className="mt-8" />

      <div className="mt-10">
        <Button block onClick={() => void submit()} disabled={sending}>
          {sending ? 'Sending…' : 'Send my request'}
        </Button>
      </div>
    </AppShell>
  );
}
